import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { adminBlogPostStats } from "@/lib/blog-stats.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { ArrowLeft, Eye, MousePointerClick, TrendingUp, Target, ExternalLink } from "lucide-react";
import { TrendCharts } from "@/components/social/TrendCharts";

export const Route = createFileRoute("/admin/blog-stats/$id")({
  component: BlogPostStats,
});

function Stat({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`h-4 w-4 ${tone}`} /> {label}
      </div>
      <div className="mt-1 font-serif text-2xl text-primary">{value}</div>
    </div>
  );
}

function BlogPostStats() {
  const { id } = Route.useParams();
  const fn = useServerFn(adminBlogPostStats);
  const [days, setDays] = useState<30 | 90>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ["blog-post-stats", id, days],
    queryFn: () => fn({ data: { id, days } }),
  });

  if (isLoading) return <div><BlogSubNav /><p className="text-sm text-muted-foreground">Loading…</p></div>;
  if (error || !data) return <div><BlogSubNav /><p className="text-sm text-rose-600">Couldn't load stats.</p></div>;

  const { post, pageUrl, tracked, totals, series } = data;

  return (
    <div>
      <BlogSubNav />
      <div className="flex items-start justify-between gap-3">
        <div>
          <Link to="/admin/blog-stats" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-3 w-3" /> All blog stats
          </Link>
          <h1 className="mt-2 font-serif text-2xl text-primary">{post.title}</h1>
          <div className="text-xs text-muted-foreground">
            /{post.slug} · {post.status}
            {post.published_at ? ` · published ${new Date(post.published_at).toLocaleDateString()}` : ""}
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs">
          {[30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d as 30 | 90)}
              className={`px-3 py-1.5 ${days === d ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
            >
              Last {d} days
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
        <a href={`/blog/${post.slug}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-accent hover:underline">
          <ExternalLink className="h-3 w-3" /> View live post
        </a>
        <Link to="/admin/posts/$id" params={{ id: post.id }} className="text-muted-foreground hover:text-primary">
          Edit post →
        </Link>
        {!tracked && (
          <span className="text-amber-600">Not yet in Search Console — Google may not have indexed this URL yet.</span>
        )}
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <Stat icon={Eye} label="Impressions" value={totals.impressions.toLocaleString()} tone="text-blue-500" />
        <Stat icon={MousePointerClick} label="Clicks" value={totals.clicks.toLocaleString()} tone="text-amber-500" />
        <Stat icon={TrendingUp} label="CTR" value={`${(totals.ctr * 100).toFixed(2)}%`} tone="text-emerald-500" />
        <Stat icon={Target} label="Avg. position" value={totals.avgPosition > 0 ? totals.avgPosition.toFixed(1) : "—"} tone="text-primary" />
      </div>

      <div className="mt-6">
        <div className="font-serif text-lg text-primary mb-3">Trend</div>
        {series.length === 0 || totals.impressions === 0 ? (
          <p className="text-sm text-muted-foreground">No Search Console data yet for this post.</p>
        ) : (
          <TrendCharts data={series} />
        )}
      </div>

      {pageUrl && (
        <p className="mt-4 text-xs text-muted-foreground">
          Tracked URL: <span className="font-mono">{pageUrl}</span>
        </p>
      )}
    </div>
  );
}
